import { gql, useLazyQuery, useQuery } from "@apollo/client";
import { Modal, Thumbnail, Button, Checkbox, RadioButton, Pagination, Layout } from "@shopify/polaris";
import React, { useCallback, useEffect, useState } from "react";
import { SketonProduct } from "./SketonProduct";
import style from "../../../../scss/module/product_sku.module.scss";
import { FilterProductWithSKU } from "./FilterProductWithSKU";
import { Graph } from "src/generated/graph";
import {
  FilterContextState,
  initialFilterState,
} from "src/pages/products/items/components/productlist/context/FilterContext";
import { ProductSelectList } from "./ProductSelectList";
import { useTranslation } from "react-i18next";

export const QUERY_PRODUCT_LIST = gql`
  query productListV2(
    $productListType: ProductListType!
    $filter: ProductListFilterV2
    $search: ProductSearch
    $page: PaginationInput
    $orderBy: ProductOrderByInput
    $viewBySku: Boolean
  ) {
    productListV2(
      productListType: $productListType
      filter: $filter
      search: $search
      page: $page
      orderBy: $orderBy
      viewBySku: $viewBySku
    ) {
      data {
        id
        picture
        code
        supplierCode
        title {
          en
          km
          zh
        }
        mark
        remoted
        status
        category {
          id
          name {
            en
          }
        }
        brand {
          id
          name
        }
        vendor {
          id
          name
        }
        tags
        price
        discount
        color
        size
        skuList {
          id
          option1
          option2
          barcode
          enabled
          stockQty
          barcodeOriginal
        }
        reach
        clicks
        soldQty
        revenue
        stockQty
        weight
        boxSize
        inputDate
        lastStockIn
      }
      pagination {
        total
      }
    }
  }
`;

export interface ProductSelectedProp {
  id: number;
  sku: number[];
}

interface Props {
  productOnly?: boolean;
  initialProductSelected: ProductSelectedProp[];
  onProductSelectedChange?: (value: ProductSelectedProp[]) => void;
  onLoadProductInfo?: (products: any[]) => void;
  code?: ProductSelectedProp[];
  singleSelect?: boolean;
  inStock?: boolean;
}

export function LoadProductWithSKU({
  productOnly,
  initialProductSelected,
  onProductSelectedChange,
  onLoadProductInfo,
  code: propCode,
  singleSelect,
  inStock,
}: Props) {
  const { t } = useTranslation();
  const [firstLoad, setFirstLoad] = useState(true);
  const [offset, setOffset] = useState(0);
  const [limit] = useState(25);
  const [open, setOpen] = useState(false);
  const [code, setCode] = useState<string | undefined>("");

  const [selected, setSelected] = useState<any[]>([]);
  const [productPreview, setProductPreview] = useState<any[]>([]);

  const [filters, setFilters] = useState<FilterContextState>(initialFilterState);

  const [queryProduct] = useLazyQuery<Graph.Query>(QUERY_PRODUCT_LIST, {
    fetchPolicy: "cache-and-network",
  });

  const { data, loading } = useQuery<Graph.Query>(QUERY_PRODUCT_LIST, {
    fetchPolicy: "cache-and-network",
    variables: {
      productListType: "APPROVED",
      filter: {
        status: filters.status,
        mark: filters.mark,
        brand: filters.brand.map((x) => Number(x)),
        categories: filters.categories.map((x) => Number(x)),
        vendor: filters.vendor.map((x) => Number(x)),
        isRemoted: filters.isRemoted === "T" ? true : filters.isRemoted === "F" ? false : undefined,
        gteDiscount: filters.gteDiscount ? Number(filters.gteDiscount) : undefined,
        gteStock: filters.gteStock ? Number(filters.gteStock) : undefined,
        lteDiscount: filters.lteDiscount ? Number(filters.lteDiscount) : undefined,
        lteStock: filters.lteStock ? Number(filters.lteStock) : undefined,
      },
      search: code ? { code } : undefined,
      viewBySku: filters.viewBySku === "F" ? false : true,
      orderBy:
        propCode && propCode?.length > 0
          ? {
              field: "DATE",
              sortBy: "DESC",
            }
          : {
              field: "DATE",
              sortBy: "DESC",
            },
      page: {
        offset: code ? 0 : offset * limit,
        limit,
        cacheTotalRow: undefined,
      },
    },
  });

  const fetchProductInfo = async () => {
    await setOffset(0);
    await setCode("");
    await setFilters(initialFilterState);
    if (onLoadProductInfo) {
      if (selected.length > 0) {
        let items = [];
        for (const x of selected) {
          const product = productPreview.find((f) => f.id === x.id);
          const sku = product ? product.skuList?.filter((f: any) => x.sku.includes(f.id)) : [];

          items.push({
            product,
            sku: sku?.map((s: any) => {
              return {
                ...s,
                color: s.option1,
                size: s.option2,
                qty: s.stockQty,
                price: product
                  ? (Number(product.price) - Number((product.discount || 0) / 100) * Number(product.price)).toFixed(2)
                  : 0,
                id: s.id,
              };
            }),
          });
        }
        onLoadProductInfo(items);
      } else {
        onLoadProductInfo([]);
      }
    }
    toggleActive();
  };

  const loadProduct = useCallback(
    (value) => {
      setSelected(value);
      setFirstLoad(selected.filter((f) => f.id > 0).length > 0 ? false : true);
      if (!!firstLoad) {
        queryProduct({
          variables: {
            productListType: "APPROVED",
            filter: {
              status: [],
              mark: [],
              brand: [],
              categories: [],
              vendor: [],
              isRemoted: undefined,
              gteDiscount: undefined,
              gteStock: undefined,
              lteDiscount: undefined,
              lteStock: undefined,
            },
            search: undefined,
            orderBy: {
              field: "ID",
              sortBy: "DESC",
              value: initialProductSelected.map((x) => Number(x.id)),
            },
            page: {
              offset: 0,
              limit: initialProductSelected.length,
              cacheTotalRow: undefined,
            },
          },
        }).then((r) => {
          const product: any = r?.data?.productListV2?.data?.filter((f: any) =>
            initialProductSelected.map((x) => x.id).includes(f.id),
          );
          setProductPreview(product);
          setFirstLoad(false);
        });
      }
    },
    [selected, firstLoad, initialProductSelected, queryProduct],
  );

  /* eslint-disable react-hooks/exhaustive-deps */
  const fetchData = useCallback(() => {
    if (initialProductSelected.length > 0) {
      setFirstLoad(true);
      loadProduct(initialProductSelected);
    } else {
      setFirstLoad(false);
    }
  }, [initialProductSelected, loadProduct]);
  /* eslint-enable react-hooks/exhaustive-deps */

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const toggleActive = useCallback(() => {
    setOpen((op) => !op);
    setOffset(0);
  }, []);

  const handleCheckedFromSku = (value: boolean, id: number, skuId: number) => {
    //--selected:
    //-- check product exists

    const product = data?.productListV2?.data?.find((f) => f.id === id);
    const p = selected.findIndex((f) => f.id === id);

    if (product) {
      let skus = product.skuList?.map((sku) => sku.id) || [];

      if (inStock && !product.remoted) {
        skus = product.skuList?.filter((sku) => (sku.stockQty || 0) > 0 && !!sku.enabled).map((sku) => sku.id) || [];
      }

      if (!!value) {
        if (p < 0) {
          setSelected((selected) => {
            const x = [...selected, { id, sku: [skuId], all: skus.length === 1 }];
            onProductSelectedChange && onProductSelectedChange(x);
            return x;
          });
          setProductPreview([...productPreview, product]);
        } else {
          const dump_product_selected = [...selected];
          dump_product_selected[p].sku.push(skuId);
          dump_product_selected[p].all = skus.length === dump_product_selected[p].sku.length;
          setSelected(dump_product_selected);
          onProductSelectedChange && onProductSelectedChange(dump_product_selected);
        }
      } else {
        const dump_product_selected = [...selected];
        dump_product_selected[p].sku = dump_product_selected[p].sku.filter((f: any) => f !== skuId);
        dump_product_selected[p].all = skus.length === dump_product_selected[p].sku.length;
        setSelected(
          dump_product_selected[p].sku.length === 0
            ? dump_product_selected.filter((f) => f.id !== id)
            : dump_product_selected,
        );
        onProductSelectedChange &&
          onProductSelectedChange(
            dump_product_selected[p].sku.length === 0
              ? dump_product_selected.filter((f) => f.id !== id)
              : dump_product_selected,
          );
      }
    } else {
      if (selected.map((x) => x.id).includes(id)) {
        if (!value) {
          const dump_product_selected = [...selected];
          dump_product_selected[p].sku = dump_product_selected[p].sku.filter((f: any) => f !== skuId);
          setSelected(
            dump_product_selected[p].sku.length === 0
              ? dump_product_selected.filter((f) => f.id !== id)
              : dump_product_selected,
          );
          onProductSelectedChange &&
            onProductSelectedChange(
              dump_product_selected[p].sku.length === 0
                ? dump_product_selected.filter((f) => f.id !== id)
                : dump_product_selected,
            );
        }
      }
    }
  };

  const handleCheckedFromProduct = useCallback(
    (value: boolean, id: number) => {
      const product = data?.productListV2?.data?.find((f) => f.id === id);
      if (product) {
        if (singleSelect) {
          if (!!value) {
            let skus = product.skuList?.map((sku) => sku.id);
            if (inStock && !product.remoted) {
              skus =
                product.skuList?.filter((sku) => (sku.stockQty || 0) > 0 && !!sku.enabled).map((sku) => sku.id) || [];
            }
            onProductSelectedChange && onProductSelectedChange([{ id, sku: skus, all: true }] as any);
            setSelected([{ id, sku: skus, all: true }] as any);
          } else {
            const x = selected.filter((f) => f.id !== id);
            onProductSelectedChange && onProductSelectedChange(x);
            setSelected(x);
          }
        } else {
          if (!!value) {
            let skus = product.skuList?.map((sku) => sku.id);
            if (inStock && !product.remoted) {
              skus =
                product.skuList?.filter((sku) => (sku.stockQty || 0) > 0 && !!sku.enabled).map((sku) => sku.id) || [];
            }
            const items = [...selected];
            const index = items.findIndex((f) => Number(f.id) === id);
            if (index >= 0) {
              items[index].sku = skus;
              items[index].all = true;
              setSelected(items);
              return;
            }
            setSelected((selected) => {
              const x = [...selected, { id, sku: skus, all: true }];
              onProductSelectedChange && onProductSelectedChange(x);
              return x;
            });
          } else {
            setSelected((selected) => {
              const x = selected.filter((f) => Number(f.id) !== Number(id));
              onProductSelectedChange && onProductSelectedChange(x);
              return x;
            });
          }
        }
        setProductPreview([...productPreview, product]);
        setCode("");
      } else {
        if (selected.map((x) => x.id).includes(id)) {
          if (!value) {
            setSelected((selected) => {
              const x = selected.filter((f) => Number(f.id) !== Number(id));
              onProductSelectedChange && onProductSelectedChange(x);
              return x;
            });
          }
        }
      }
    },
    [data, selected, singleSelect, onProductSelectedChange, inStock, productPreview],
  );

  const activator = (
    <Button size="slim" onClick={toggleActive} disabled={!!firstLoad} loading={!!firstLoad}>
      {t("choose_product")}
    </Button>
  );

  const current_position = offset === 0 ? 1 : offset * limit;
  const last_position = offset === 0 ? limit : offset * limit + limit;

  return (
    <Modal
      size="large"
      title={t("product_picker")}
      open={open}
      activator={activator}
      onClose={toggleActive}
      footer={
        <div style={{ display: "flex", justifyContent: "space-between" }}>
          <div>
            <span className="mr-3">
              <Button onClick={toggleActive}>{t("cancel")}</Button>
            </span>{" "}
            <Button
              variant="primary"
              onClick={() => {
                fetchProductInfo();
              }}
            >
              {t("add")}
            </Button>
          </div>
        </div>
      }
    >
      <Modal.Section flush>
        <FilterProductWithSKU search={code} onSearchChange={setCode} onFilterChange={setFilters} filter={filters} />
        <div style={{ width: "100%" }}>
          <Pagination
            type="table"
            label={
              singleSelect
                ? ((
                    <span className="ml-1">
                      {`${current_position}-${last_position} of ${data?.productListV2?.pagination?.total} products`}
                    </span>
                  ) as any)
                : ((
                    <span className="ml-1">
                      <Checkbox
                        label={`${current_position}-${last_position} of ${data?.productListV2?.pagination?.total} products`}
                        checked={
                          data?.productListV2?.data?.filter((f) => !!selected.map((s) => s.id).includes(f.id))
                            .length === data?.productListV2?.data?.length
                        }
                        onChange={(v) => {
                          if (!!v) {
                            for (const x of data?.productListV2?.data as any) {
                              handleCheckedFromProduct(true, x.id || 0);
                            }
                          } else {
                            const select_not_remove = selected.filter(
                              (f) => !data?.productListV2?.data?.map((d) => d.id).includes(f.id),
                            );
                            onProductSelectedChange && onProductSelectedChange(select_not_remove);
                            setSelected(select_not_remove);
                          }
                        }}
                      />
                    </span>
                  ) as any)
            }
            hasNext={(data?.productListV2?.data?.length || 0) >= limit}
            hasPrevious={offset > 0}
            onNext={() => setOffset(offset + 1)}
            onPrevious={() => setOffset(offset - 1)}
          />
        </div>
        <div style={{ position: "relative", height: window.innerHeight / 1.5, overflow: "hidden" }}>
          <Layout>
            <Layout.Section>
              {loading || (!data && <SketonProduct rowsCount={25} />)}
              {data && (
                <div
                  style={{
                    borderRight: "0.0625rem solid #dde0e4",
                    height: window.innerHeight / 1.5,
                    overflowY: "auto",
                  }}
                >
                  {data.productListV2?.data?.map((x, i: number) => {
                    const product_checked = selected.find((f) => f.id === x.id);
                    return (
                      <div key={i}>
                        <div className={style.basic_product}>
                          <div className={style.info}>
                            {singleSelect ? (
                              <RadioButton
                                label=""
                                labelHidden
                                checked={product_checked ? true : false}
                                onChange={(value) => handleCheckedFromProduct(value, x.id || 0)}
                              />
                            ) : (
                              <Checkbox
                                label=""
                                labelHidden
                                checked={product_checked ? (product_checked.all ? true : "indeterminate") : false}
                                onChange={(value) => handleCheckedFromProduct(value, x.id || 0)}
                              />
                            )}
                            <div className="ml-3">
                              <Thumbnail alt={x.code || ""} source={x.picture || ""} size="small" />
                            </div>
                            <div className="ml-3">
                              <small>{x.supplierCode}</small>
                              <br />
                              <small style={{ fontWeight: "bold" }}>${x.price}</small>
                              <br />
                              <small>L192: {x.code}</small>
                            </div>
                          </div>
                          {productOnly && (
                            <React.Fragment>
                              <div className={style.qty}>
                                <small>{x.stockQty} available</small>
                              </div>
                              <div className={style.price}>
                                <small>
                                  ${(Number(x.price) - Number((x.discount || 0) / 100) * Number(x.price)).toFixed(2)}
                                </small>
                              </div>
                            </React.Fragment>
                          )}
                        </div>
                        {!productOnly && (
                          <React.Fragment>
                            {inStock && x.remoted === false
                              ? x.skuList
                                  ?.filter((sku) => (sku.stockQty || 0) > 0 && !!sku.enabled)
                                  .map((sku: any, index: any) => {
                                    return (
                                      <div id={x.id + ""} key={index} className={style.basic_product}>
                                        <div className={style.sku}>
                                          <Checkbox
                                            label=""
                                            labelHidden
                                            checked={
                                              product_checked ? (product_checked.sku as any[]).includes(sku.id) : false
                                            }
                                            onChange={(value) => handleCheckedFromSku(value, x.id || 0, sku.id || 0)}
                                          />
                                          <div className="ml-5">
                                            <small>
                                              {sku.option1?.trim() || "No Color"} / {sku.option2?.trim() || "No Size"}
                                            </small>
                                          </div>
                                        </div>
                                        <div className={style.qty}>
                                          <small>{sku.stockQty} available</small>
                                        </div>
                                        <div className={style.price}>
                                          <small>
                                            $
                                            {(
                                              Number(x.price) -
                                              Number((x.discount || 0) / 100) * Number(x.price)
                                            ).toFixed(2)}
                                          </small>
                                        </div>
                                      </div>
                                    );
                                  })
                              : x.skuList?.map((sku: any, index: any) => {
                                  return (
                                    <div id={x.id + ""} key={index} className={style.basic_product}>
                                      <div className={style.sku}>
                                        <Checkbox
                                          label=""
                                          labelHidden
                                          checked={
                                            product_checked ? (product_checked.sku as any[]).includes(sku.id) : false
                                          }
                                          onChange={(value) => handleCheckedFromSku(value, x.id || 0, sku.id || 0)}
                                        />
                                        <div className="ml-5">
                                          <small>
                                            {sku.option1?.trim() || "No Color"} / {sku.option2?.trim() || "No Size"}
                                          </small>
                                        </div>
                                      </div>
                                      <div className={style.qty}>
                                        <small>{sku.stockQty} available</small>
                                      </div>
                                      <div className={style.price}>
                                        <small>
                                          $
                                          {(
                                            Number(x.price) -
                                            Number((x.discount || 0) / 100) * Number(x.price)
                                          ).toFixed(2)}
                                        </small>
                                      </div>
                                    </div>
                                  );
                                })}
                          </React.Fragment>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </Layout.Section>
            <Layout.Section variant="oneThird">
              <div
                style={{
                  marginLeft: "-5%",
                  position: "sticky",
                  top: 0,
                  height: window.innerHeight / 1.5,
                  overflowY: "auto",
                }}
              >
                <ProductSelectList
                  productOnly={productOnly}
                  skuId={selected.length ? selected.map((x) => x.sku).flat() : []}
                  productSelected={selected.filter((x) => !!x.id || x.id > 0).map((x) => Number(x.id))}
                  onRemove={(id) => {
                    handleCheckedFromProduct(false, id);
                  }}
                  onRemoveSku={(sku, id) => {
                    handleCheckedFromSku(false, id, sku);
                  }}
                />
              </div>
            </Layout.Section>
          </Layout>
        </div>
        <Pagination
          type="table"
          label={`${current_position}-${last_position} of ${data?.productListV2?.pagination?.total} products`}
          hasNext={(data?.productListV2?.data?.length || 0) >= limit}
          hasPrevious={offset > 0}
          onNext={() => setOffset(offset + 1)}
          onPrevious={() => setOffset(offset - 1)}
        />
      </Modal.Section>
    </Modal>
  );
}
